import { InfiniteQueryObserverResult } from '@tanstack/react-query';
import { useState } from 'react';

type UseAutocompleteInfiniteProps<T> = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<InfiniteQueryObserverResult<T, Error>>;
};

type UseAutocompleteInfiniteReturn = {
  isOpen: boolean;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  handleOpen: () => void;
  handleClose: () => void;
};

/**
 * This hook is used to handle the infinite scroll of an autocomplete component.
 * When the scroll ratio (scrollHeight - scrollTop / clientHeight) is less than 1.2, it fetches more data.
 */
export const useAutocompleteInfinite = <T>({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseAutocompleteInfiniteProps<T>): UseAutocompleteInfiniteReturn => {
  const [isOpen, setIsOpen] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const scrollRatio = (target.scrollHeight - target.scrollTop) / target.clientHeight;
    if (scrollRatio <= 1.2 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    // Delay closing so that pending click events on dropdown items can fire
    // before the dropdown unmounts. Without this, a blur-triggered close
    // (from BaseAutocomplete's onOpenChange) removes the dropdown before
    // the click handler executes, causing item selections to be silently lost.
    requestAnimationFrame(() => {
      if (!isFetchingNextPage) {
        setIsOpen(false);
      }
    });
  };

  return {
    isOpen,
    handleScroll,
    handleOpen,
    handleClose,
  };
};
